/**
 * Which currency a set of figures is stated in — and what was left out of it.
 *
 * The API never adds two currencies together: a report picks one, filters its
 * aggregates to it, and lists the others it found in `currency.present`. That
 * guard is only half a guard if the screen does not say so, because the figures
 * themselves look exactly the same as a single-currency business's. Someone
 * reading "₦840,000" has no way to tell that a second country's takings sat
 * outside the total unless it is written down.
 *
 * So: nothing at all for the single-currency business — which is every business
 * today, and adding a banner saying "figures are in naira" to a screen where
 * every figure already starts with ₦ is noise. The notice appears when a second
 * currency turns up in the window, and stays visible while the reader is on a
 * currency they chose themselves, so the switch that got them there is still
 * there to get them back.
 */

import type { ReactNode } from 'react';
import { Coins } from 'lucide-react';
import { findCurrency, type ReportCurrency } from '@cenox/shared';
import { Button, cn } from './ui';

interface Props {
  currency: ReportCurrency;
  /**
   * Re-request the same figures in another currency. Omitted where the
   * endpoint has no currency parameter — the dashboard — in which case the
   * notice explains without offering a switch it cannot honour.
   */
  onSelect?: ((code: string) => void) | undefined;
  /**
   * True when the reader picked this currency rather than the server choosing
   * it. Without this the switcher would vanish the moment they landed on a
   * currency with no records in the window — which is exactly when they need
   * it to get back.
   */
  selected?: boolean;
}

/** "NGN (Nigerian Naira)" reads better than a bare code to someone unsure. */
function currencyName(code: string): string {
  return findCurrency(code)?.name ?? code;
}

export function CurrencyNotice({ currency, onSelect, selected = false }: Props): ReactNode {
  if (!currency.isMixed && !selected) return null;

  const others = currency.present.filter((code) => code !== currency.code);
  const empty = !currency.present.includes(currency.code);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex gap-2.5">
        <Coins className="mt-0.5 size-4 shrink-0 text-amber-700" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-amber-900">
            {empty ? (
              <>
                Nothing was recorded in <strong>{currency.code}</strong> in this period, so every
                figure below is zero.
              </>
            ) : (
              <>
                Every figure here is in <strong>{currency.code}</strong> ({currencyName(currency.code)}
                ).{' '}
                {others.length === 1
                  ? `${others[0]} records in this period are not counted in them.`
                  : others.length > 1
                    ? `Records in ${others.join(', ')} are not counted in them.`
                    : ''}
              </>
            )}
          </p>

          {(currency.isMixed || empty) && (
            <p className="mt-1 text-xs text-amber-800">
              Amounts in different currencies are never added together — there is no exchange rate
              behind these totals. Each currency is reported in its own terms.
            </p>
          )}

          {onSelect && currency.present.length > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-amber-900">Show:</span>
              {currency.present.map((code) => (
                <Button
                  key={code}
                  size="sm"
                  variant={code === currency.code ? 'primary' : 'secondary'}
                  className={cn(code === currency.code && 'pointer-events-none')}
                  aria-current={code === currency.code ? 'true' : undefined}
                  onClick={() => onSelect(code)}
                >
                  {code}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
